// src/pages/Dashboard/UsedTokenByDate.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Typography, CircularProgress, Alert, Button, ButtonGroup } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import backendURL from "../../config";
import { useChatbots } from "../../contexts/ChatbotContext"; // Import context hook

Chart.register(...registerables);

export default function UsedTokensChart() {
    const { activeChatbot } = useChatbots(); // Get active chatbot
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [viewMode, setViewMode] = useState("bySession");
    const [processedData, setProcessedData] = useState(null);

    useEffect(() => {
        document.title = "Used Tokens Chart | KlonIT Web Application";
        // Only fetch data if an active chatbot is selected
        if (activeChatbot) {
            fetchData();
        } else {
            setChartData(null); // Clear chart if no chatbot is active
        }
    }, [activeChatbot]); // Refetch when the active chatbot changes

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("access_token");
            const headers = { Authorization: `Bearer ${token}` };
            
            // FIX: Pass the active chatbot's ID as a query parameter
            const params = { chatbot_id: activeChatbot.id };

            const response = await axios.get(`${backendURL}/get-used-token-by-date`, { headers, params });
            const responseData = response.data.data; // Data is now a direct object

            processChartData(responseData);
        } catch (error) {
            console.error("Error fetching token data:", error);
            setError("Failed to fetch token data for the selected chatbot.");
        } finally {
            setLoading(false);
        }
    };

    // ... (processChartData, updateChartData, handleViewChange, options remain the same)
    const processChartData = (data) => {
        const bySessionData = data;

        const aggregatedDailyData = {};
        data.forEach((item) => {
            const date = item.created_date;
            if (!aggregatedDailyData[date]) {
                aggregatedDailyData[date] = { input: 0, output: 0, total: 0 };
            }
            aggregatedDailyData[date].input += item.input_token;
            aggregatedDailyData[date].output += item.output_token;
            aggregatedDailyData[date].total += item.total_token;
        });

        const monthlyData = {};
        const yearlyData = {};

        data.forEach((item) => {
            const [year, month] = item.created_date.split("-");
            const yearMonthKey = `${year}-${month}`;

            if (!monthlyData[yearMonthKey]) {
                monthlyData[yearMonthKey] = { input: 0, output: 0, total: 0 };
            }
            monthlyData[yearMonthKey].input += item.input_token;
            monthlyData[yearMonthKey].output += item.output_token;
            monthlyData[yearMonthKey].total += item.total_token;

            if (!yearlyData[year]) {
                yearlyData[year] = { input: 0, output: 0, total: 0 };
            }
            yearlyData[year].input += item.input_token;
            yearlyData[year].output += item.output_token;
            yearlyData[year].total += item.total_token;
        });

        setProcessedData({
            bySession: bySessionData,
            aggregatedDaily: aggregatedDailyData,
            monthly: monthlyData,
            yearly: yearlyData,
        });
        
        // Ensure the chart updates with the new data based on the current view mode
        updateChartData(viewMode, { bySession: bySessionData, aggregatedDaily: aggregatedDailyData, monthly: monthlyData, yearly: yearlyData }[viewMode]);
    };

    const updateChartData = (mode, data) => {
        let chartLabels, inputTokens, outputTokens, totalTokens;

        if (mode === "bySession") {
            chartLabels = data.map((item) => item.created_date);
            inputTokens = data.map((item) => item.input_token);
            outputTokens = data.map((item) => item.output_token);
            totalTokens = data.map((item) => item.total_token);
        } else {
            const aggregatedData = Object.entries(data);
            chartLabels = aggregatedData.map(([key]) => key);
            inputTokens = aggregatedData.map(([_, value]) => value.input);
            outputTokens = aggregatedData.map(([_, value]) => value.output);
            totalTokens = aggregatedData.map(([_, value]) => value.total);
        }

        setChartData({
            labels: chartLabels,
            datasets: [
                { label: "Input Tokens", data: inputTokens, backgroundColor: "rgba(75, 192, 192, 0.6)" },
                { label: "Output Tokens", data: outputTokens, backgroundColor: "rgba(255, 159, 64, 0.6)" },
                { label: "Total Tokens", data: totalTokens, backgroundColor: "rgba(153, 102, 255, 0.6)" },
            ],
        });
    };

    const handleViewChange = (mode) => {
        setViewMode(mode);
        if (processedData) {
            updateChartData(mode, processedData[mode]);
        }
    };
    
    const options = {
        plugins: { legend: { display: true } },
        responsive: true,
        scales: { x: { stacked: true }, y: { stacked: true } },
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <div className="card">
                    <div className="card-body">
                        <Box sx={{ p: 4 }}>
                            <Typography variant="h4" gutterBottom>
                                Used Tokens for {activeChatbot?.chatbot_name || '...'}
                            </Typography>
                            <ButtonGroup variant="contained" sx={{ mb: 2 }}>
                                <Button onClick={() => handleViewChange("bySession")} color={viewMode === "bySession" ? "primary" : "inherit"}>By Session</Button>
                                <Button onClick={() => handleViewChange("aggregatedDaily")} color={viewMode === "aggregatedDaily" ? "primary" : "inherit"}>Daily</Button>
                                <Button onClick={() => handleViewChange("monthly")} color={viewMode === "monthly" ? "primary" : "inherit"}>Monthly</Button>
                                <Button onClick={() => handleViewChange("yearly")} color={viewMode === "yearly" ? "primary" : "inherit"}>Yearly</Button>
                            </ButtonGroup>
                            
                            {loading && <CircularProgress />}
                            {error && <Alert severity="error">{error}</Alert>}
                            {!loading && !error && !chartData && <Typography>No token data available for this chatbot.</Typography>}

                            {chartData && (
                                <Box sx={{ mt: 4 }}>
                                    <Bar data={chartData} options={options} />
                                </Box>
                            )}
                        </Box>
                    </div>
                </div>
            </div>
        </div>
    );
}